import { Cell } from "./Cell";
import { BookingData } from "../model/BookingData";
import { IBookingOptions } from "../model/BookingData";

export class CalendarView
{
	private readonly _header: Element;
	private readonly _tbody: HTMLElement;
	private readonly _cells: Cell[];
	private readonly _options: IBookingOptions;
	private _cb: (cell: Cell) => void;

	constructor(element: HTMLElement, idx: number, options: IBookingOptions)
	{
		this._options = options;

		this._header = element.getElementsByClassName("year-month")[idx];
		const table = element.getElementsByClassName("calendar")[idx];
		const thead = this.createTableHeader();

		table.appendChild(thead);

		this._tbody = document.createElement("tbody");
		table.appendChild(this._tbody);
		this._cells = [];
	}

	public render(y: number, m: number): void
	{
		this.updateHeader(y, m);
		this.updateTableBody(y, m);
	}

	public setCellListener(cb: (cell: Cell) => void): void
	{
		this._cb = cb;
	}

	public setAvailability(data: BookingData): void
	{
		for (const cell of this._cells)
		{
			if (data.isInPast(cell.y, cell.m, cell.d))
			{
				cell.setInPast();
			}
			else if (data.isBookedFull(cell.time))
			{
				cell.setBookedFull();
			}
			else if (data.isBookedCheckOut(cell.time))
			{
				cell.setBookedCheckOut();
				cell.initButton(this._cb);
			}
			else if (data.isBookedCheckIn(cell.time))
			{
				cell.setBookedCheckIn();
				cell.initButton(this._cb);
			}
			else
			{
				cell.initButton(this._cb);
			}
		}
	}

	public reserve(from: Date | null, to: Date | null): void
	{
		const fromTs = from ? from.getTime() : 0;
		const toTs = to ? to.getTime() : 0;

		for (const cell of this._cells)
		{
			cell.clearReserves();

			const cellTs = cell.date.getTime();
			if (cellTs === fromTs)
			{
				cell.setReserveCheckIn();
			}
			else if (fromTs !== 0 && toTs !== 0 && fromTs < cellTs && toTs > cellTs)
			{
				cell.setReserveFull();
			}
			else if (cellTs === toTs)
			{
				cell.setReserveCheckOut();
			}
		}
	}

	private createTableHeader(): HTMLElement
	{
		const thead = document.createElement("thead");

		const tr = document.createElement("tr");
		thead.appendChild(tr);

		const event = new Date();
		let date = 1;
		let i = 0;
		while (i < 7)
		{
			event.setDate(date++);
			if (event.getDay() === this._options.dayIds[0] || i > 0)
			{
				const th = document.createElement("th");
				th.innerHTML = event.toLocaleDateString(this._options.locale, {weekday: "short"});
				tr.appendChild(th);
				i++;
			}
		}

		return thead;
	}

	private updateHeader(y: number, m: number): void
	{
		const event = new Date();
		event.setFullYear(y);
		event.setMonth(m);
		this._header.innerHTML = event.toLocaleDateString(this._options.locale, {year: "numeric", month: "long"});
	}

	private updateTableBody(y: number, m: number): void
	{
		this._cells.length = 0;
		this._tbody.innerHTML = "";

		const now = new Date(y, m, 1);
		const firstDayId = now.getDay();
		const maxDayId = new Date(y, m + 1, 0).getDate();

		let d = 1;

		while (d <= maxDayId)
		{
			const tr = document.createElement("tr");
			this._tbody.appendChild(tr);

			for (const day of this._options.dayIds)
			{
				const cell = new Cell(y, m, d);
				tr.appendChild(cell.elem);

				if ((d === 1 && day !== firstDayId) || d > maxDayId)
				{
					cell.setAsDummy(); // no day inactive cell
				}
				else
				{
					cell.showDate();
					this._cells.push(cell);
					d++;
				}
			}
		}
	}
}
